/**
 * @kgc/service-worksheet - Worksheet DTOs
 * Story 17-1: Munkalap CRUD
 *
 * Zod validációs sémák
 */

import { z } from 'zod';
import {
  IWorksheetSummary,
  WorksheetPriority,
  WorksheetType,
} from '../interfaces/worksheet.interface';

/**
 * Munkalap létrehozás DTO
 */
export const CreateWorksheetSchema = z.object({
  /** Partner azonosító */
  partnerId: z.string().uuid('Érvénytelen partner azonosító'),
  /** Munkalap típus */
  type: z.nativeEnum(WorksheetType, {
    errorMap: () => ({ message: 'Érvénytelen munkalap típus' }),
  }),
  /** Prioritás (opcionális, default: NORMAL) */
  priority: z.nativeEnum(WorksheetPriority).default(WorksheetPriority.NORMAL),
  /** Gép megnevezés */
  deviceName: z
    .string()
    .min(2, 'Gép megnevezés minimum 2 karakter')
    .max(200, 'Gép megnevezés maximum 200 karakter'),
  /** Gép sorozatszám (opcionális) */
  deviceSerialNumber: z.string().max(100).optional(),
  /** Hiba leírás */
  faultDescription: z
    .string()
    .min(5, 'Hiba leírás minimum 5 karakter')
    .max(2000, 'Hiba leírás maximum 2000 karakter'),
  /** Belső megjegyzés (opcionális) */
  internalNote: z.string().max(2000).optional(),
  /** Felelős szerelő ID (opcionális) */
  assignedToId: z.string().uuid().optional(),
  /** Javítási költség limit (opcionális, FR107) */
  costLimit: z.number().min(0).max(10_000_000).optional(),
  /** Becsült befejezés (opcionális) */
  estimatedCompletionDate: z.coerce.date().optional(),
  /** Kapcsolódó bérlés ID (opcionális) */
  rentalId: z.string().uuid().optional(),
  /**
   * Gépkategória KÓDJA a felvételkor (`MachineCategory.code`, pl. `KISGEP`).
   * A BE oldja fel UUID-ra és `machineCategoryId`-ként perzisztálja — enélkül a
   * `GET :id/inspection-fee` mindig az `inspectionFeeMissing` ágra fut.
   * A `max(50)` a séma alakja (`sales.prisma:513 code VarChar(50)`), nem üzleti korlát.
   */
  machineCategoryCode: z.string().max(50).optional(),
});

export type CreateWorksheetDto = z.infer<typeof CreateWorksheetSchema>;

/**
 * Munkalap frissítés DTO
 */
export const UpdateWorksheetSchema = z.object({
  /** Prioritás */
  priority: z.nativeEnum(WorksheetPriority).optional(),
  /** Gép megnevezés */
  deviceName: z.string().min(2).max(200).optional(),
  /** Gép sorozatszám */
  deviceSerialNumber: z.string().max(100).optional(),
  /** Hiba leírás */
  faultDescription: z.string().min(5).max(2000).optional(),
  /** Diagnosztika */
  diagnosis: z.string().max(2000).optional(),
  /** Elvégzett munka */
  workPerformed: z.string().max(2000).optional(),
  /** Belső megjegyzés */
  internalNote: z.string().max(2000).optional(),
  /** Felelős szerelő ID */
  assignedToId: z.string().uuid().optional().nullable(),
  /** Javítási költség limit */
  costLimit: z.number().min(0).max(10_000_000).optional().nullable(),
  /** Becsült befejezés */
  estimatedCompletionDate: z.coerce.date().optional().nullable(),

  /**
   * Q-T171: a „Díj fizetve" gomb ezt küldi (`PATCH /worksheets/:id`).
   * A mező 2026-04-09 óta HIÁNYZOTT a whitelistről → `forbidNonWhitelisted: true`
   * miatt MINDEN hívás 400-at adott, és a felületen nincs kerülőút → a bevizsgálási
   * díj rögzítése 3,5 hónapja lehetetlen volt. A séma (`inspection_fee_paid`) és a
   * payment-gate logika végig kész volt — csak a WRITE-út hiányzott.
   */
  inspectionFeePaid: z.boolean().optional(),
});

export type UpdateWorksheetDto = z.infer<typeof UpdateWorksheetSchema>;

/**
 * Munkalap tétel létrehozás DTO
 */
export const CreateWorksheetItemSchema = z.object({
  /** Cikk ID (alkatrész esetén) */
  productId: z.string().uuid().optional(),
  /** Tétel megnevezés */
  description: z.string().min(2, 'Megnevezés minimum 2 karakter').max(500),
  /** Mennyiség */
  quantity: z.number().positive('Mennyiség pozitív kell legyen'),
  /** Egységár (nettó) */
  unitPrice: z.number().min(0, 'Egységár nem lehet negatív'),
  /** ÁFA kulcs (%) */
  vatRate: z.number().min(0).max(100).default(27),
  /** Tétel típus */
  itemType: z.enum(['PART', 'LABOR', 'OTHER', 'EXTERNAL_COST']),
});

export type CreateWorksheetItemDto = z.infer<typeof CreateWorksheetItemSchema>;

/**
 * Munkalap lista szűrő DTO
 */
export const WorksheetFilterSchema = z.object({
  /** Státusz szűrő */
  status: z.string().optional(),
  /** Típus szűrő */
  type: z.nativeEnum(WorksheetType).optional(),
  /** Partner ID szűrő */
  partnerId: z.string().uuid().optional(),
  // ⛔ QA-170 (2026-09-14): a `assignedToId` szűrő KIVÉVE — a `Worksheet` modellnek nincs
  //    ilyen oszlopa, a repo sosem kötötte `where`-be. A jövőbeli felelős-szűrő a tétel-soron
  //    (`WorksheetItem.performedById`) épül, nem ezen a fejléc-mezőn.
  /** Dátum tól */
  dateFrom: z.coerce.date().optional(),
  /** Dátum ig */
  dateTo: z.coerce.date().optional(),
  /** Keresés (munkalap szám, gép, partner) */
  search: z.string().optional(),
  /** Limit */
  limit: z.number().min(1).max(100).default(20),
  /** Offset */
  offset: z.number().min(0).default(0),
  /** Batch-intake azonosító szűrő (page10-B) */
  intakeBatchId: z.string().uuid().optional(),
});

export type WorksheetFilterDto = z.infer<typeof WorksheetFilterSchema>;

/**
 * Batch-intake egy géptétele (page10-B, 2026-06-12)
 */
export const CreateWorksheetBatchItemSchema = z.object({
  /** Gép megnevezés */
  deviceName: z.string().min(1),
  /** Gép sorozatszám (opcionális) */
  deviceSerialNumber: z.string().min(1).optional(),
  /** Hiba leírás */
  faultDescription: z.string().min(1),
  /** Javítási költség limit (opcionális) */
  costLimit: z.number().min(0).optional(),
  /** Belső megjegyzés (opcionális) */
  internalNote: z.string().optional(),
});

export type CreateWorksheetBatchItemDto = z.infer<typeof CreateWorksheetBatchItemSchema>;

/**
 * Batch-intake kérés — több gép egyszerre egy partnertől (page10-B, 2026-06-12)
 */
export const CreateWorksheetBatchSchema = z.object({
  /** Partner azonosító */
  partnerId: z.string().uuid(),
  /** Munkalap típus (opcionális) */
  type: z.string().optional(),
  /** Prioritás (opcionális) */
  priority: z.string().optional(),
  /** Gép tételek (min 1, max 26 = A–Z suffix) */
  items: z.array(CreateWorksheetBatchItemSchema).min(1).max(26),
});

export type CreateWorksheetBatchDto = z.infer<typeof CreateWorksheetBatchSchema>;

/**
 * Batch-létrehozás során visszaadott egy munkalap wire-kontraktusa (page10-B, 2026-06-12)
 */
export const BatchCreatedWorksheetSchema = z
  .object({
    /** Munkalap UUID */
    id: z.string().uuid(),
    /** Munkalap szám */
    worksheetNumber: z.string(),
    /** Batch azonosító */
    intakeBatchId: z.string().uuid(),
    /** Státusz */
    status: z.string(),
    /** Gép megnevezés */
    deviceName: z.string(),
    /** Sorozatszám (opcionális) */
    deviceSerialNumber: z.string().optional(),
  })
  .passthrough();

export type BatchCreatedWorksheetDto = z.infer<typeof BatchCreatedWorksheetSchema>;

/**
 * POST /worksheets/batch 201 válasz kontraktusa (page10-B, 2026-06-12)
 */
export const WorksheetBatchCreateResponseSchema = z.object({
  data: z.object({
    /** A batch azonosítója */
    intakeBatchId: z.string().uuid(),
    /** Létrehozott munkalapok */
    worksheets: z.array(BatchCreatedWorksheetSchema).min(1),
  }),
});

export type WorksheetBatchCreateResponseDto = z.infer<typeof WorksheetBatchCreateResponseSchema>;

/**
 * Munkalap költség-összesítő READ-DTO (W1 seam-freeze, 2026-06-08)
 *
 * A `WorksheetItemService.calculateSummary()` által számolt `IWorksheetSummary`
 * shape Zod-kontraktusa — ez a FE `worksheet/02` cost-summary panel **sync-event-je**
 * (read-seam-first: a FE erre pin-eli a proto-parity spec-et + MSW-mockot, futó BE nélkül).
 * Minden összeg nettó/bruttó HUF `number` (a repo `Decimal` → `Number` map-pel adja).
 * Az `externalCostNetAmount` az EXTERNAL_COST (alvállalkozói díj) külön sora — NEM az `otherNetAmount`-ban.
 */
export const WorksheetCostSummarySchema = z.object({
  /** Munkalap ID */
  worksheetId: z.string().uuid(),
  /** Alkatrész költség (nettó, HUF) */
  partsNetAmount: z.number(),
  /** Munkadíj (nettó, HUF) */
  laborNetAmount: z.number(),
  /** Egyéb költség (nettó, HUF) */
  otherNetAmount: z.number(),
  /** Alvállalkozói díj (nettó, HUF) */
  externalCostNetAmount: z.number(),
  /** Teljes nettó összeg (HUF) */
  totalNetAmount: z.number(),
  /** Teljes bruttó összeg (HUF) */
  totalGrossAmount: z.number(),
  /** ÁFA összeg (HUF) */
  vatAmount: z.number(),
});

export type WorksheetCostSummaryDto = z.infer<typeof WorksheetCostSummarySchema>;

/**
 * Compile-time kontraktus-őr: a Zod read-DTO és a service-produced `IWorksheetSummary`
 * MINDKÉT irányban egyezik. Ha bármelyik mező elcsúszik → a generic-constraint
 * megsérül → **typecheck FAIL** (nem silent seam-drift). [[feedback-aspirational-baseline-measurement-gate]]
 */
type _AssertExtends<TBase, TDerived extends TBase> = TDerived;
export type _WorksheetCostSummaryContractFwd = _AssertExtends<
  WorksheetCostSummaryDto,
  IWorksheetSummary
>;
export type _WorksheetCostSummaryContractBwd = _AssertExtends<
  IWorksheetSummary,
  WorksheetCostSummaryDto
>;

/**
 * `GET /worksheets/:id/summary` teljes válasz-kontraktus (SRV-F1-2)
 *
 * A cost-summary + a vizsgadíj-jóváírás (`inspectionFeeCredit`). Ez a FE `worksheet/02`
 * cost-summary panel **élő seam-anchora** — a végpont MÁR létezik
 * (`worksheet-crud.controller.ts` `getSummary`), ez annak a típusos kontraktusa.
 */
export const WorksheetSummaryResponseSchema = WorksheetCostSummarySchema.extend({
  /** Vizsgadíj-jóváírás (SRV-F1-2) — null, ha nincs jóváírás */
  inspectionFeeCredit: z
    .object({
      /** Jóváírás összege (HUF) */
      amount: z.number(),
      /** Jóváírás leírása */
      description: z.string(),
    })
    .nullable(),
});

export type WorksheetSummaryResponseDto = z.infer<typeof WorksheetSummaryResponseSchema>;

/**
 * Munkalap detail READ-DTO wire-kontraktus (nagykör-1 seam-freeze, 2026-06-11)
 *
 * A `GET /worksheets/:id` response `data` mezőjének Zod-kontraktusa a **wire-formátumon**
 * (JSON: a Prisma `Date` → ISO-8601 string). Ez a FE `worksheet/11` detail page (6-tab +
 * Előzmény-timeline) **sync-event-je**: a FE erre pin-eli az MSW-mockot + a proto-parity
 * spec-et (pinnelt fixture: `__fixtures__/worksheet-detail.contract-fixture.json`).
 *
 * Append-only éjjeli kontrakt (Winston F4): új mező CSAK additívan, shape-mutáció /
 * rename → reggeli Javo-gate. A lifecycle-timestampek (`diagnosedAt`/`startedAt`/
 * `deliveredAt`/`customerNotifiedAt`/`closedAt`) a page11 Előzmény-timeline-t táplálják.
 *
 * ⚠️ Ismert mapper-gap (NEM kontrakt-tag, ne építs rá MSW-ben): az `IWorksheet`
 * deklarálja, de a repo `toWorksheetDomain` MA nem adja ki: `assignedToId`,
 * `workPerformed`, `estimatedCost`, `requiresQuote`, `qrCode`, `customerStatusToken`.
 * Pótlásuk külön slice (mapper-bővítés + kontrakt-append + fixture-bump egyben).
 */
export const WorksheetDetailReadSchema = z.object({
  /** Egyedi azonosító (UUID) */
  id: z.string().uuid(),
  /** Tenant azonosító (multi-tenancy) */
  tenantId: z.string().uuid(),
  /** Munkalap szám (ML-YYYY-NNNN) */
  worksheetNumber: z.string(),
  /** Munkalap típus */
  type: z.nativeEnum(WorksheetType),
  /** Aktuális státusz (a repo context-aware SZAMLAZANDO-mappinggel adja) */
  status: z.string(),
  /** Prioritás */
  priority: z.nativeEnum(WorksheetPriority),
  /** Partner azonosító */
  partnerId: z.string().uuid(),
  /** Gép megnevezés (brand + model derived) */
  deviceName: z.string(),
  /** Gép sorozatszám */
  deviceSerialNumber: z.string().optional(),
  /** Hiba leírás (ügyfél által) */
  faultDescription: z.string(),
  /** Diagnosztika (szerelő által) */
  diagnosis: z.string().optional(),
  /** Belső megjegyzés (csak dolgozóknak) */
  internalNote: z.string().optional(),
  /** Javítási költség limit (FR107) */
  costLimit: z.number().optional(),
  /** Becsült befejezés dátum (wire: ISO-8601) */
  estimatedCompletionDate: z.string().datetime().optional(),
  /** Felvétel dátuma (wire: ISO-8601) */
  receivedAt: z.string().datetime(),
  /** Munka befejezésének dátuma (wire: ISO-8601) */
  completedAt: z.string().datetime().optional(),
  // Lifecycle timestamps (nagykör-1 contract-append, 2026-06-11 — page11 Előzmény-timeline)
  /** Diagnózis elkészültének dátuma (wire: ISO-8601) */
  diagnosedAt: z.string().datetime().optional(),
  /** Javítás megkezdésének dátuma (wire: ISO-8601) */
  startedAt: z.string().datetime().optional(),
  /** Gép kiadásának dátuma (wire: ISO-8601) */
  deliveredAt: z.string().datetime().optional(),
  /** Ügyfél értesítve (tárolási díj trigger, wire: ISO-8601) */
  customerNotifiedAt: z.string().datetime().optional(),
  /** Adminisztratív lezárás dátuma (wire: ISO-8601) */
  closedAt: z.string().datetime().optional(),
  /** Kapcsolódó bérlés ID */
  rentalId: z.string().uuid().optional(),
  /** Gép kategória (N-SRV-2) */
  machineCategoryId: z.string().uuid().optional(),
  /** Várakozási lista pozíció (Epic 17-7) */
  queuePosition: z.number().optional(),
  /**
   * Q-T180 (🔨 Hephaistos review-findingja a #1048-on): a bevizsgálási díj mezői.
   * A mapper (`toWorksheetDomain`) mostantól kitölti őket, DE ez a séma sima
   * `z.object` (nincs `.passthrough()`), tehát a zod-default STRIP: a nem deklarált
   * mező CSENDBEN kiesett volna a parse-olt objektumból. Következmény enélkül:
   * (1) a `worksheet-detail-read.contract.spec` zölden átmegy, de NEM őrzi a mezőt;
   * (2) a FE-típus (`z.infer`) nem tartalmazza → `as any` kellene hozzá, pontosan az,
   * amit a #1048 épp kitakarított a controllerből.
   * A `paid` is itt van, nem csak az összeg: a mapper MINDKETTŐT adja.
   */
  inspectionFee: z.number().nullable().optional(),
  inspectionFeePaid: z.boolean().optional(),
  /** Tárolási díj kezdete (Epic 17-8, wire: ISO-8601) */
  storageStartDate: z.string().datetime().optional(),
  /** Tárolási napidíj (HUF) */
  storageDailyFee: z.number().optional(),
  /** Tárolási díj összesen (HUF) */
  storageTotalFee: z.number().optional(),
  /** Szerződéses óradíj (Story 58-9) */
  effectiveHourlyRate: z.number().optional(),
  /** Óradíj forrása */
  hourlyRateSource: z.string().optional(),
  /** Óradíj szerződés ID */
  hourlyRateContractId: z.string().uuid().optional(),
  /** Óradíj felülírás indoklása */
  hourlyRateOverrideReason: z.string().optional(),
  /** Garanciális igény link (SRV-FG-3) */
  warrantyClaimId: z.string().uuid().optional(),
  /** Garanciális-e */
  isWarranty: z.boolean().optional(),
  /** Létrehozó user */
  createdBy: z.string(),
  /** Létrehozás dátum (wire: ISO-8601) */
  createdAt: z.string().datetime(),
  /** Módosítás dátum (wire: ISO-8601) */
  updatedAt: z.string().datetime(),
});

export type WorksheetDetailReadDto = z.infer<typeof WorksheetDetailReadSchema>;

/**
 * `GET /worksheets/:id` teljes válasz-kontraktus: `{ data: <worksheet> }` wrapper
 * (API-konvenció). Az MSW-mock + a contract.spec EZT parse-olja.
 */
export const WorksheetDetailResponseSchema = z.object({
  data: WorksheetDetailReadSchema,
});

export type WorksheetDetailResponseDto = z.infer<typeof WorksheetDetailResponseSchema>;

/**
 * Worksheet státusz-történet wire-kontraktus (d-tétel, 2026-06-12)
 *
 * A `GET /worksheets/:id/status-history` válasza — a page11 Előzmény-timeline
 * user-attribúciós rétege (ki váltott státuszt, mikor, miért). A derived-timestamp
 * timeline (5 lifecycle-mező) marad a fallback üres history esetén.
 * A `tenantId` SZÁNDÉKOSAN nincs a wire-en (minimal-disclosure, 4-lens review).
 */
export const WorksheetStatusHistoryEntrySchema = z.object({
  /** Bejegyzés azonosító */
  id: z.string().uuid(),
  /** Munkalap azonosító */
  worksheetId: z.string().uuid(),
  /** Előző (domain-)státusz — az első bejegyzésnél hiányzik */
  fromStatus: z.string().optional(),
  /** Új (domain-)státusz */
  toStatus: z.string(),
  /** Indoklás (a changeStatus reason-je) */
  note: z.string().optional(),
  /** A váltást végző user azonosítója */
  changedByUserId: z.string().uuid().optional(),
  /** A váltást végző user neve (User.name join, append-only — page11 timeline) */
  changedByUserName: z.string().optional(),
  /** A váltás időpontja (wire: ISO-8601) */
  changedAt: z.string().datetime(),
});

export type WorksheetStatusHistoryEntryDto = z.infer<typeof WorksheetStatusHistoryEntrySchema>;

/** `GET /worksheets/:id/status-history` teljes válasz: `{ data: [...] }`, changedAt desc */
export const WorksheetStatusHistoryResponseSchema = z.object({
  data: z.array(WorksheetStatusHistoryEntrySchema),
});

export type WorksheetStatusHistoryResponseDto = z.infer<
  typeof WorksheetStatusHistoryResponseSchema
>;
